import { useEffect, useMemo } from 'react';

import { PROPORTIONAL_SAMPLING_CONFIG } from '@/components/nodeGraph/config/graphConfig';
import {
  calculateProportionalDistribution,
  ClusterDistribution,
} from '@/components/nodeGraph/utils/proportionalDistribution';
import { Filters } from '@/hooks/useAssets';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { Asset, GraphQuery, TreeNode } from '@/types';
import { useMergeStatus, useQueries } from '@/utils/api';
import { logger } from '@/utils/logger';

interface ProportionalAssetSampling {
  assets: Asset[];
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  distributions: ClusterDistribution[];
  totalAssetCount: number;
  status: 'idle' | 'pending' | 'success' | 'error';
  hasError: boolean;
  assetFilters: Filters;
  setAssetFilters: (filters: Filters | ((prev: Filters) => Filters)) => void;
}

/**
 * Hook for proportional asset sampling based on AS name clustering
 *
 * This hook:
 * 1. First fetches AS name statistics to understand the distribution
 * 2. Calculates proportional allocation for each cluster
 * 3. Fetches top N assets for each cluster based on allocation
 * 4. Returns combined assets that represent all clusters proportionally
 */
export function useProportionalAssets(
  clusterField: 'asname' | 'asnumber',
  filters: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  maxNodes: number = 1000,
  enabled: boolean = true
): ProportionalAssetSampling {
  // Step 1: Fetch AS statistics to understand distribution
  const { data: asStatistics, status: statsStatus } = useMy(
    {
      resource: 'statistic',
      query: clusterField === 'asname' ? 'asset#asname' : 'asset#asnumber',
    },
    {
      enabled,
    }
  );

  // Calculate proportional distribution once we have statistics
  const calculatedDistributions = useMemo(() => {
    if (!asStatistics || asStatistics.length === 0) {
      return [];
    }

    // Filter out invalid AS values and convert to counts object
    const clusterCounts: Record<string, number> = {};

    asStatistics.forEach(stat => {
      const value = stat.value?.trim();
      const count = parseInt(stat.data || '0', 10);

      // Skip invalid values
      if (!value || value === '' || value === 'null' || value === 'undefined') {
        return;
      }

      // Skip "Non-Routable" and "Non Routed" values
      const valueLower = value.toLowerCase();
      if (valueLower === 'non-routable' || valueLower === 'non routed') {
        return;
      }

      // For asnumber, skip 0 (non-routable)
      if (
        clusterField === 'asnumber' &&
        (value === '0' || parseInt(value, 10) === 0)
      ) {
        return;
      }

      clusterCounts[value] = count;
    });

    // Calculate proportional distribution
    const distributions = calculateProportionalDistribution(
      clusterCounts,
      maxNodes
    );

    return distributions;
  }, [asStatistics, clusterField, maxNodes]);

  // Step 2: Fetch assets for each cluster based on proportional allocation
  // We'll create multiple queries, one for each cluster
  const clusterQueries = useMemo(() => {
    if (calculatedDistributions.length === 0) {
      return [];
    }

    return calculatedDistributions.map(dist => ({
      clusterName: dist.clusterName,
      targetSize: dist.targetSampleSize,
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            // Apply the cluster filter
            {
              field: clusterField,
              operator: '=',
              value: dist.clusterName,
            },
            // Apply any additional filters from parent
            ...(filters?.advancedFilters?.status
              ? [
                  {
                    field: 'status',
                    operator: '=',
                    value: filters.advancedFilters.status[0],
                  },
                ]
              : []),
          ],
        },
        limit: dist.targetSampleSize,
        page: 0,
      } as GraphQuery,
    }));
  }, [calculatedDistributions, clusterField, filters]);

  // Step 3: Execute parallel queries for each cluster using useQueries
  const axios = useAxios({});

  const clusterQueriesResults = useQueries({
    queries: clusterQueries.map(clusterQuery => ({
      queryKey: [
        'asset',
        'proportional',
        clusterField,
        clusterQuery.clusterName,
        clusterQuery.targetSize,
        JSON.stringify(filters),
      ],
      queryFn: async () => {
        if (!enabled) return [];

        try {
          const { data } = await axios({
            method: 'POST',
            url: '/my',
            params: {
              tree: true,
              label: 'asset',
            },
            data: clusterQuery.query,
          });

          // Extract assets from tree response
          if (data && Array.isArray(data)) {
            const assets = data
              .map((tree: TreeNode) => {
                const entities = Object.values(tree?.node?.result || {}).find(
                  e => Array.isArray(e)
                ) as unknown as Asset[];
                return entities?.[0];
              })
              .filter(Boolean) as Asset[];

            if (PROPORTIONAL_SAMPLING_CONFIG.enableDebugLogging) {
              logger.log(
                `[ProportionalAssets] Fetched ${assets.length} assets from cluster "${clusterQuery.clusterName}" (target: ${clusterQuery.targetSize})`
              );
            }

            return assets;
          }

          return [];
        } catch (error) {
          logger.error(
            `[ProportionalAssets] Error fetching cluster "${clusterQuery.clusterName}":`,
            error
          );
          // Return empty array on error to allow other clusters to load
          return [];
        }
      },
      enabled: enabled && calculatedDistributions.length > 0,
      staleTime: PROPORTIONAL_SAMPLING_CONFIG.statisticsCacheTTL,
    })),
  });

  // Combine all query results into single asset array
  const assets = useMemo(() => {
    const allAssets = clusterQueriesResults
      .flatMap(result => result.data || [])
      .filter(Boolean) as Asset[];

    // Deduplicate by asset key
    const uniqueAssets = Array.from(
      new Map(allAssets.map(asset => [asset.key, asset])).values()
    );

    if (
      PROPORTIONAL_SAMPLING_CONFIG.enableDebugLogging &&
      uniqueAssets.length > 0
    ) {
      logger.log(
        `[ProportionalAssets] Combined ${uniqueAssets.length} unique assets from ${clusterQueries.length} clusters`
      );
    }

    return uniqueAssets;
  }, [clusterQueriesResults, clusterQueries.length]);

  // Check loading and error states
  const isLoadingAssets = clusterQueriesResults.some(
    result => result.isLoading
  );
  const hasError = clusterQueriesResults.some(result => result.isError);

  // Merge statistics and asset loading states
  const status = useMergeStatus(
    statsStatus,
    isLoadingAssets ? 'pending' : hasError ? 'error' : 'success'
  );

  // Debug logging for distribution comparison
  useEffect(() => {
    if (
      assets.length > 0 &&
      calculatedDistributions.length > 0 &&
      PROPORTIONAL_SAMPLING_CONFIG.enableDebugLogging
    ) {
      const actualDistribution: Record<string, number> = {};

      assets.forEach(asset => {
        const clusterValue =
          clusterField === 'asname'
            ? asset.asname || asset.metadata?.asname
            : asset.asnumber || asset.metadata?.asnumber;

        if (clusterValue) {
          const key = String(clusterValue);
          actualDistribution[key] = (actualDistribution[key] || 0) + 1;
        }
      });

      logger.group('📊 Proportional Sampling Results');
      logger.log('Target distributions:', calculatedDistributions);
      logger.log('Actual distribution:', actualDistribution);
      logger.log('Total assets loaded:', assets.length);
      logger.groupEnd();
    }
  }, [assets, calculatedDistributions, clusterField]);

  return {
    assets,
    isLoading: status === 'pending',
    hasNextPage: false, // No pagination for proportional sampling
    fetchNextPage: () => Promise.resolve(undefined), // No-op for proportional sampling
    distributions: calculatedDistributions,
    totalAssetCount: calculatedDistributions.reduce(
      (sum, d) => sum + d.totalCount,
      0
    ),
    status,
    hasError,
    assetFilters: filters || ({ advancedFilters: {} } as Filters),
    setAssetFilters: (_filters: Filters | ((prev: Filters) => Filters)) => {}, // No-op for proportional sampling (filters passed in)
  };
}

/**
 * Hook specifically for AS name proportional sampling in graph explorer
 */
export function useProportionalAssetsForGraph(
  filters: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  maxNodes: number = 1000,
  enabled: boolean = true
) {
  return useProportionalAssets('asname', filters, maxNodes, enabled);
}
